import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { Controller, useWatch, type Control } from 'react-hook-form';

import OfferFormField from '@/domain/offers/components/OfferFormField';
import { type OfferFormValues } from '@/domain/offers/types';
import { OFFER_LIMITS, getOfferCouponHint } from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';

interface Props {
  control: Control<OfferFormValues>;
}

/**
 * "O que o atleta ganha": an optional coupon and its rules (SR19 SO03.1: the app shows until when
 * it is valid and these rules under the coupon). The hint follows the buttons the offer has.
 */
const OfferBenefitFields = ({ control }: Props) => {
  const t = useTranslate('domain.offers.components.OfferBenefitFields');
  const enabled = useWatch({ control, name: 'benefit.enabled' });
  const actions = useWatch({ control, name: 'actions' });

  return (
    <Stack gap={2}>
      <Controller
        name="benefit.enabled"
        control={control}
        rules={{ deps: ['actions'] }}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
                onBlur={field.onBlur}
                inputRef={field.ref}
              />
            }
            label={
              <Stack gap={0.25}>
                <Typography variant="body2" fontWeight={600}>
                  {t('enable')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('enableHint')}
                </Typography>
              </Stack>
            }
            sx={{ alignItems: 'flex-start', ml: 0, gap: 1 }}
          />
        )}
      />
      {enabled ? (
        <>
          <OfferFormField
            control={control}
            name="benefit.code"
            label={t('code')}
            placeholder={t('codePlaceholder')}
            maxChars={OFFER_LIMITS.couponMax}
            hint={t(`codeHint.${getOfferCouponHint(actions ?? [])}`)}
          />
          <OfferFormField
            control={control}
            name="benefit.terms"
            label={t('terms')}
            optionalLabel={t('optional')}
            placeholder={t('termsPlaceholder')}
            maxChars={OFFER_LIMITS.couponTermsMax}
            hint={t('termsHint')}
            multiline
            minRows={2}
          />
        </>
      ) : null}
    </Stack>
  );
};

export default OfferBenefitFields;
